import { useState, useEffect } from 'react';
import moment from 'moment';
import {
  ArrowCta,
  FormCard,
  Heading,
  Text,
  Input,
  FormControl,
  Link,
  VStack,
} from 'components/ui';
import { Box, useBreakpointValue } from '@chakra-ui/react';
import Select from 'react-select';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTracking } from '../../hooks/useTracking';
import useEligibilityRequest from '../../api/onboarding/mutations/useEligibilityRequest';
import { HTTP_TOO_MANY_REQUESTS } from '../../hooks/useApi';
import {
  TRACKING_CLICK_ELIGIBILITY_CTA,
  TRACKING_ELIGIBILITY_CHECK_DENIED,
  TRACKING_ELIGIBILITY_CHECK_SCREEN,
  TRACKING_ELIGIBILITY_CHECK_VERIFIED,
  TRACKING_ERROR_ON_ELIGIBILITY_CHECK,
  TRACKING_VISITED_ELIGIBILITY_CHECK,
} from '../OnboardingForm/constants';
import { useAuth } from '../../hooks';
import { useAffiliate } from '../../api/affiliates/useAffiliate';
import { FullBenefitsModal } from './FullBenefitsModal';
import { AffiliateType } from '../../models/affiliate';
import CardInput from './components/CardInput';
import { PlanHolderInfo } from './components/PlanHolderInfo';
import {
  DETAILS_MISMATCH_MESSAGE,
  EligibilityError,
  RATE_LIMIT_HEADING,
  RATE_LIMIT_MESSAGE,
} from './components/EligibilityError';
import { useGoogleTagManager } from '../../hooks/useGoogleTagManager';
import { useFormData } from '../../utils/formReducer';
import HeaderNav from '../HeaderNav/HeaderNav';
import { StickyCtaLayout } from '../ui/StickyCtaLayout';
import { mergeSearchParams } from '../../utils/searchParams';

const DEFAULT_SUBSCRIBER_RELATIONSHIP_VALUE = 'self';

export const Eligibility = () => {
  const { page: pageView, track } = useTracking();
  const { state: formDataFromState } = useFormData();
  const { trackEventInGoogleTagManager } = useGoogleTagManager();
  const { user: authenticatedUser } = useAuth();
  const { state, search } = useLocation();

  const { affiliate } = useAffiliate({
    affiliateId: state?.affiliate || formDataFromState?.selected_affiliate,
  });
  const navigate = useNavigate();
  const { mutation } = useEligibilityRequest();

  const isMobile = useBreakpointValue({
    base: true,
    lg: false,
  });

  const [inputView, setInputView] = useState(null);

  const [formData, setData] = useState({
    firstName: state?.firstName,
    lastName: state?.lastName,
  });

  const [formHasChanged, setFormHasChanged] = useState(false);
  const [errors, setErrors] = useState({});
  const [eligibilityDeniedMessage, setEligibilityDeniedMessage] =
    useState(null);
  const [showFullRelease, setShowFullRelease] = useState(false);
  const shouldCollectPlanHolderInfo =
    !!formData?.relationship &&
    formData.relationship !== DEFAULT_SUBSCRIBER_RELATIONSHIP_VALUE;
  const { userId, userEmail } = state || {};

  useEffect(() => {
    if (!userId) {
      navigate(`/${search}`);
    } else {
      pageView(TRACKING_VISITED_ELIGIBILITY_CHECK);
    }

    // if the user is authenticated, meaning they just logged in
    // then we want to default the first and last name to their registered values.
    // Prefer legal first name from router state (set during onboarding) over the
    // stored first_name, which may be a preferred name rather than legal name.
    if (authenticatedUser) {
      setData({
        firstName: state?.firstName || authenticatedUser?.first_name,
        lastName: authenticatedUser?.last_name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedUser?.first_name, authenticatedUser?.last_name, userId]);

  useEffect(() => {
    trackEventInGoogleTagManager(TRACKING_ELIGIBILITY_CHECK_SCREEN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateRegex = /(\d{4})-(\d{2})-(\d{2})/;

  const validate = () => {
    let validationErrors = {};

    if (!formData.firstName) {
      validationErrors = {
        ...validationErrors,
        firstName: 'First name is required',
      };
    }

    if (!formData.lastName) {
      validationErrors = {
        ...validationErrors,
        lastName: 'Last name is required',
      };
    }

    if (!formData.memberId?.trim()) {
      validationErrors = {
        ...validationErrors,
        memberId: 'Member ID (or Contract number) is required',
      };
    }

    if (!!affiliate?.group_id_required && !formData.groupId?.trim()) {
      validationErrors = {
        ...validationErrors,
        groupId: 'Group ID is required',
      };
    }
    if (formData.groupId?.length > 32) {
      validationErrors = {
        ...validationErrors,
        groupId: 'Group ID must not be more than 32 characters',
      };
    }

    if (shouldCollectPlanHolderInfo) {
      if (!formData.subscriberDob) {
        validationErrors = {
          ...validationErrors,
          subscriberDob: "Plan holder's date of birth is required",
        };
      } else if (!formData.subscriberDob?.match(dateRegex)) {
        validationErrors = {
          ...validationErrors,
          subscriberDob:
            "Plan holder's date of birth must be in YYYY-MM-DD format",
        };
      } else if (moment().diff(formData.subscriberDob, 'years') < 18) {
        validationErrors = {
          ...validationErrors,
          subscriberDob: 'Plan holder must be 18+ in order to use Ayble',
        };
      }

      if (!formData.subscriberMemberId) {
        validationErrors = {
          ...validationErrors,
          subscriberMemberId: "Plan holder's member ID is required",
        };
      }

      if (!formData.subscriberFirstName) {
        validationErrors = {
          ...validationErrors,
          subscriberFirstName: "Plan holder's first name is required",
        };
      }

      if (!formData.subscriberLastName) {
        validationErrors = {
          ...validationErrors,
          subscriberLastName: "Plan holder's last name is required",
        };
      }

      if (!formData.subscriberGender) {
        validationErrors = {
          ...validationErrors,
          subscriberGender: "Plan holder's gender is required",
        };
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    } else {
      setErrors({});
      return true;
    }
  };

  const populateCardData = (data) => {
    const capitalizeValue = (value) =>
      value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

    const { memberNumber, groupNumber, memberName } = data.details;

    const memberId = memberNumber.value;
    const groupId = groupNumber.value;
    const memberNameFull = memberName.value;
    const memberNameParsed = memberNameFull.split(' ');

    setData((prevData) => ({
      ...prevData,
      ...(memberId && { memberId }),
      ...(groupId && { groupId }),
      ...(memberNameParsed && {
        firstName: capitalizeValue(memberNameParsed[0]),
        lastName: capitalizeValue(
          memberNameParsed[memberNameParsed.length - 1]
        ),
      }),
    }));

    setFormHasChanged(true);
  };

  const onSubmit = async () => {
    track(TRACKING_CLICK_ELIGIBILITY_CTA, { userId });

    setEligibilityDeniedMessage(null);
    setFormHasChanged(false);

    if (validate()) {
      const {
        memberId,
        firstName,
        lastName,
        groupId,
        relationship,
        subscriberMemberId,
        subscriberDob,
        subscriberGender,
        subscriberFirstName,
        subscriberLastName,
      } = formData;

      try {
        const subscriber = {
          member_id: subscriberMemberId,
          date_of_birth: subscriberDob,
          gender: subscriberGender,
          first_name: subscriberFirstName,
          last_name: subscriberLastName,
        };

        const data = await mutation.mutateAsync({
          user_id: userId,
          member_id: memberId.replaceAll(' ', ''),
          user_email: userEmail,
          first_name: firstName,
          last_name: lastName,
          group_id: groupId,
          relationship_to_subscriber:
            relationship || DEFAULT_SUBSCRIBER_RELATIONSHIP_VALUE,
          ...(shouldCollectPlanHolderInfo && { subscriber }),
        });

        if (data?.eligible) {
          track(TRACKING_ELIGIBILITY_CHECK_VERIFIED, { userId });
          navigate(
            `/schedule-appointment${mergeSearchParams(search, { userId })}`,
            { state: { userEmail } }
          );
        } else {
          track(TRACKING_ELIGIBILITY_CHECK_DENIED, { userId });
          if (data?.route_path === 'ineligible') {
            navigate(`/ineligible${search}`, {
              state: {
                message: data?.message,
                userId,
              },
            });
          } else {
            setEligibilityDeniedMessage(
              data?.message || DETAILS_MISMATCH_MESSAGE
            );
          }
        }
      } catch (error) {
        track(TRACKING_ERROR_ON_ELIGIBILITY_CHECK, {
          userId,
          error,
        });

        console.error(error);

        if (error?.status === HTTP_TOO_MANY_REQUESTS) {
          navigate(`/ineligible${search}`, {
            state: {
              heading: RATE_LIMIT_HEADING,
              message: RATE_LIMIT_MESSAGE,
              userId,
            },
          });
        }
      }
    }
  };

  const isScanningOnMobile = inputView === 'scan' && isMobile;

  const planHolderError =
    errors.subscriberMemberId ||
    errors.subscriberFirstName ||
    errors.subscriberLastName ||
    errors.subscriberDob ||
    errors.subscriberGender;

  const requiredFieldsFilled =
    !!formData.memberId &&
    (!affiliate?.group_id_required || !!formData.groupId) &&
    (!shouldCollectPlanHolderInfo ||
      (!!formData.subscriberMemberId &&
        !!formData.subscriberFirstName &&
        !!formData.subscriberLastName &&
        !!formData.subscriberDob &&
        !!formData.subscriberGender));

  const isSubmitDisabled =
    mutation.isLoading || !formHasChanged || !requiredFieldsFilled;

  const errorMessage = eligibilityDeniedMessage ? (
    eligibilityDeniedMessage
  ) : mutation.isError ? (
    <>
      We are unable to connect with your insurance provider to confirm
      eligibility. We will keep trying and will email you as soon as we confirm
      you are eligible for care with Ayble. Stay tuned! Please contact{' '}
      <Link
        _text={{ textTransform: 'none' }}
        href="mailto:support@ayblehealth.com"
      >
        support@ayblehealth.com
      </Link>{' '}
      with any questions.
    </>
  ) : null;

  return (
    <>
      <StickyCtaLayout
        actionArea={
          !isScanningOnMobile && (
            <ArrowCta
              disabled={isSubmitDisabled}
              loading={mutation.isLoading}
              onClick={onSubmit}
            >
              {errorMessage ? 'Try again' : 'Connect Insurance & Book Visit'}
            </ArrowCta>
          )
        }
        header={!isScanningOnMobile && <HeaderNav />}
        maxWidth="formWidth"
      >
        {!isScanningOnMobile && (
          <Heading
            fontWeight="normal"
            mb={'22px'}
            textAlign={'center'}
            textStyle="heading.h2"
          >
            {affiliate?.affiliate_type === AffiliateType.healthPlan
              ? `Share your ${affiliate?.name} card details`
              : `Ayble is a covered benefit for most ${affiliate?.name} employees. This means you can pay $0 out-of-pocket!`}
          </Heading>
        )}

        <Box
          bgColor={'feather.200'}
          mb={errorMessage ? '18px' : '45px'}
          pb={'15px'}
          position={'relative'}
          pt={'14px'}
          px={'15px'}
          rounded={'16px'}
          w={'100%'}
        >
          <Text color={'blueGray.600'} fontSize={'13px'} mb={'10px'} ml={'2px'}>
            Skip manual entry:
          </Text>

          <CardInput
            onInputViewChange={setInputView}
            onScanSuccess={populateCardData}
            userId={userId}
          />
        </Box>

        {!isScanningOnMobile && (
          <VStack alignItems={'stretch'} gap={'18px'} w={'100%'}>
            {errorMessage && (
              <EligibilityError>{errorMessage}</EligibilityError>
            )}

            <FormCard w={'100%'}>
              {affiliate?.candid_eligibility && (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={'18px'}
                  w="100%"
                >
                  <FormControl
                    disabled={mutation.isLoading}
                    required
                    invalid={errors.firstName}
                  >
                    <FormControl.Label>
                      First Name (exactly as appears on card)
                    </FormControl.Label>
                    <Input
                      type="text"
                      maxWidth={'100%'}
                      value={formData.firstName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setData({ ...formData, firstName: value });
                        setFormHasChanged(true);
                        const { firstName, ...otherErrors } = errors;
                        setErrors(otherErrors);
                      }}
                    />
                    <FormControl.ErrorMessage mb={4}>
                      <Text fontSize={'md'}>{errors.firstName}</Text>
                    </FormControl.ErrorMessage>
                  </FormControl>

                  <FormControl
                    disabled={mutation.isLoading}
                    required
                    invalid={errors.lastName}
                  >
                    <FormControl.Label>
                      Last Name (exactly as appears on card)
                    </FormControl.Label>
                    <Input
                      type="text"
                      maxWidth={'100%'}
                      value={formData.lastName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setData({ ...formData, lastName: value });
                        setFormHasChanged(true);
                        const { lastName, ...otherErrors } = errors;
                        setErrors(otherErrors);
                      }}
                    />
                    <FormControl.ErrorMessage mb={4}>
                      <Text fontSize={'md'}>{errors.lastName}</Text>
                    </FormControl.ErrorMessage>
                  </FormControl>
                </Box>
              )}

              <FormControl
                disabled={mutation.isLoading}
                required
                invalid={errors.memberId}
              >
                <FormControl.Label>
                  Member ID (or Contract number)
                </FormControl.Label>

                <Input
                  type="text"
                  maxWidth={'100%'}
                  value={formData.memberId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setData({ ...formData, memberId: value });
                    setFormHasChanged(true);
                    const { memberId, ...otherErrors } = errors;
                    setErrors(otherErrors);
                  }}
                />
                <FormControl.ErrorMessage mb={4}>
                  <Text fontSize={'md'}>{errors.memberId}</Text>
                </FormControl.ErrorMessage>
              </FormControl>

              {(affiliate?.candid_eligibility ||
                affiliate?.group_id_required) && (
                <FormControl
                  disabled={mutation.isLoading}
                  required
                  invalid={errors.groupId}
                >
                  <FormControl.Label>
                    Group ID{' '}
                    {!affiliate.group_id_required
                      ? '(Required if your employer covers Ayble)'
                      : ''}
                  </FormControl.Label>

                  <Input
                    maxWidth={'100%'}
                    type="input"
                    value={formData.groupId}
                    onChange={(e) => {
                      const value = e.target.value;
                      setData({ ...formData, groupId: value });
                      setFormHasChanged(true);
                      const { groupId: groupIdErr, ...otherErrs } = errors;
                      setErrors(otherErrs);
                    }}
                  />

                  <FormControl.ErrorMessage mb={4}>
                    <Text fontSize={'md'}>{errors.groupId}</Text>
                  </FormControl.ErrorMessage>
                </FormControl>
              )}

              <FormControl disabled={mutation.isLoading} required>
                <FormControl.Label>
                  What is your relationship to the subscriber (insured)?
                </FormControl.Label>

                <Select
                  components={{ IndicatorSeparator: () => null }}
                  isDisabled={mutation.isLoading}
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: '100%',
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      width: '100%',
                      borderRadius: '8px',
                      height: 48,
                      padding: '0px 8px',
                      backgroundColor: 'rgba(4, 51, 56, 0.05)',
                      borderColor: 'transparent',
                      boxShadow: 'none',
                    }),
                  }}
                  defaultValue={{
                    label: 'Self',
                    value: DEFAULT_SUBSCRIBER_RELATIONSHIP_VALUE,
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  options={[
                    { label: 'Self', value: 'self' },
                    { label: 'Spouse', value: 'spouse' },
                    { label: 'Child', value: 'child' },
                    { label: 'Other', value: 'other' },
                  ]}
                  aria-label="Choose Relationship with subscriber"
                  onChange={(selection) => {
                    setData({ ...formData, relationship: selection.value });
                    setFormHasChanged(true);
                  }}
                />
              </FormControl>

              {shouldCollectPlanHolderInfo && (
                <Box w="100%">
                  <FormControl
                    required={true}
                    disabled={mutation.isLoading}
                    invalid={!!planHolderError}
                  >
                    <PlanHolderInfo
                      memberId={formData?.subscriberMemberId ?? ''}
                      dob={formData?.subscriberDob ?? ''}
                      gender={formData?.subscriberGender ?? ''}
                      firstName={formData?.subscriberFirstName ?? ''}
                      lastName={formData?.subscriberLastName ?? ''}
                      onPlanHolderInfoChange={({
                        memberId,
                        dob,
                        gender,
                        firstName,
                        lastName,
                      }) => {
                        setData({
                          ...formData,
                          subscriberMemberId: memberId,
                          subscriberDob: dob,
                          subscriberGender: gender,
                          subscriberFirstName: firstName,
                          subscriberLastName: lastName,
                        });
                        setFormHasChanged(true);
                        const {
                          subscriberMemberId: subscriberMemberIdErr,
                          subscriberDob: subscriberDobErr,
                          subscriberGender: subscriberGenderErr,
                          subscriberFirstName: subscriberFirstNameErr,
                          subscriberLastName: subscriberLastNameErr,
                          ...otherErrs
                        } = errors;
                        setErrors(otherErrs);
                      }}
                    />
                    <FormControl.ErrorMessage>
                      <Text fontSize={'md'}>{planHolderError}</Text>
                    </FormControl.ErrorMessage>
                  </FormControl>
                </Box>
              )}
            </FormCard>

            {Object.keys(errors).length > 0 && (
              <Box>
                <Text fontSize={'md'} mt={3} color="red.500">
                  Need help? Contact{' '}
                  <Link
                    _text={{ textTransform: 'none' }}
                    href={'mailto:support@ayblehealth.com'}
                  >
                    support@ayblehealth.com
                  </Link>
                  .
                </Text>
              </Box>
            )}
          </VStack>
        )}
      </StickyCtaLayout>

      <FullBenefitsModal
        isOpen={showFullRelease}
        onClose={() => setShowFullRelease(false)}
      />
    </>
  );
};

export default Eligibility;
